import {
  h, ref,
} from "vue";
import {
  ElMessage,
} from "element-plus";
import AbstractDialogTitle from "~/components/abstract-dialog/title.vue";
import DialogBody from "~/services/payment-verification-dialog/body.vue";
import DialogFooter from "~/services/payment-verification-dialog/footer.vue";
import {
  useDialogStoreV2,
} from "~/components/abstract-dialog/useDialogStoreV2";
import {
  confirmCancelPendingOrder,
} from "~/services/cancel-pending-order-dialog";
import {
  waitForPaymentFulfillment,
} from "~/services/payment-verification-dialog/payment-fulfillment";

export function usePaymentVerificationDialog(
  orderDetails: any,
  paymentInfo: any,
  onSuccessCallback: () => Promise<void>,
) {
  const dialogStoreV2 = useDialogStoreV2();
  const {$t} = useNuxtApp();
  const apiFetch = useApiFetch();
  const sessionStore = useSessionStore();

  const resolution = defer<[boolean]>();
  const paymentSuccessful = ref(false);
  const refreshLoading = ref(false);
  const cancelLoading = ref(false);
  const currentOrderDetails = ref(orderDetails);
  const currentPaymentInfo = ref(paymentInfo);
  const cancellationRemainingSeconds = ref(0);
  let statusCheckInFlight = false;
  let closeTimer: ReturnType<typeof setTimeout> | null = null;
  let cancellationTimer: ReturnType<typeof setInterval> | null = null;
  let lastCancellationCheckSecond: number | null = null;

  const cancellationPending = computed(() => currentOrderDetails.value?.status === "cancellation_pending");

  const stopCancellationTimer = () => {
    if (cancellationTimer) {
      clearInterval(cancellationTimer);
      cancellationTimer = null;
    }
  };

  const completePayment = async () => {
    if (paymentSuccessful.value) {
      return;
    }
    paymentSuccessful.value = true;
    stopCancellationTimer();
    await onSuccessCallback();
    closeTimer = setTimeout(() => {
      resolution.resolve([true]);
      hideDialog();
    }, 2000);
  };

  const checkOrderStatus = async () => {
    if (statusCheckInFlight || paymentSuccessful.value) {
      return "unchanged";
    }
    statusCheckInFlight = true;
    try {
      const result = await waitForPaymentFulfillment(currentOrderDetails.value.invoiceNumber);
      if (result.fulfillmentCompleted) {
        await completePayment();
        return "completed";
      }
      if (result.paymentReceived) {
        return "processing";
      }
      return "unchanged";
    } finally {
      statusCheckInFlight = false;
    }
  };

  const pollCancellationStatus = async () => {
    try {
      const invoiceNumber = encodeURIComponent(currentOrderDetails.value.invoiceNumber);
      const data = await apiFetch<any>(`/api/payments/orders/${invoiceNumber}`);
      currentOrderDetails.value = data?.order ?? data;
      currentPaymentInfo.value = data?.payment ?? currentPaymentInfo.value;
      const status = String(currentOrderDetails.value?.status ?? "").toLowerCase();
      if (status === "paid" && currentOrderDetails.value?.fulfillmentStatus === "completed") {
        await completePayment();
        return;
      }
      if (status === "canceled" || status === "voided") {
        stopCancellationTimer();
        await sessionStore.fetchRegistrationPaymentStatus(true);
        ElMessage.success($t("PAYMENT_REMINDER.CANCEL_ORDER_SUCCESS"));
        resolution.resolve([false]);
        hideDialog();
      }
    } catch {
      return;
    }
  };

  const tickCancellationCountdown = () => {
    const effectiveAt = currentOrderDetails.value?.cancellationEffectiveAt;
    if (!effectiveAt) {
      cancellationRemainingSeconds.value = 0;
      stopCancellationTimer();
      return;
    }
    const milliseconds = new Date(effectiveAt).getTime() - Date.now();
    cancellationRemainingSeconds.value = Math.max(0, Math.ceil(milliseconds / 1000));
    if (
      cancellationRemainingSeconds.value !== lastCancellationCheckSecond
      && (cancellationRemainingSeconds.value === 0 || cancellationRemainingSeconds.value % 10 === 0)
    ) {
      lastCancellationCheckSecond = cancellationRemainingSeconds.value;
      void pollCancellationStatus();
    }
    if (cancellationRemainingSeconds.value === 0) {
      stopCancellationTimer();
    }
  };

  const startCancellationTimer = () => {
    stopCancellationTimer();
    lastCancellationCheckSecond = null;
    tickCancellationCountdown();
    if (cancellationRemainingSeconds.value > 0) {
      lastCancellationCheckSecond = cancellationRemainingSeconds.value;
      cancellationTimer = setInterval(tickCancellationCountdown, 1000);
    }
  };

  const refreshStatus = async () => {
    refreshLoading.value = true;
    try {
      const status = await checkOrderStatus();
      if (status === "processing") {
        ElMessage.info($t("PAYMENT_REMINDER.PAYMENT_STATUS_UPDATED"));
      } else if (status === "unchanged") {
        ElMessage.info($t("COMMON.NO_CHANGES"));
      }
    } catch {
      ElMessage.error($t("PAYMENT_REMINDER.REFRESH_STATUS_FAILED"));
    } finally {
      refreshLoading.value = false;
    }
  };

  const cancelOrder = async () => {
    const confirmed = await confirmCancelPendingOrder({
      title: $t("PAYMENT_REMINDER.CANCEL_ORDER_TITLE"),
      message: $t("PAYMENT_REMINDER.CANCEL_ORDER_CONFIRM"),
      confirmLabel: $t("PAYMENT_REMINDER.CANCEL_ORDER"),
    });

    if (!confirmed) {
      return;
    }

    cancelLoading.value = true;
    try {
      const invoiceNumber = encodeURIComponent(currentOrderDetails.value.invoiceNumber);
      await apiFetch(`/api/payments/orders/${invoiceNumber}/cancel`, {
        method: "POST",
      });
      await sessionStore.fetchRegistrationPaymentStatus(true);
      const data = await apiFetch<any>(`/api/payments/orders/${invoiceNumber}`);
      currentOrderDetails.value = data?.order ?? data;
      currentPaymentInfo.value = data?.payment ?? currentPaymentInfo.value;
      startCancellationTimer();
      ElMessage.info($t("PAYMENT_REMINDER.PAYMENT_CANCELLATION_REQUESTED"));
    } catch {
      ElMessage.error($t("PAYMENT_REMINDER.CANCEL_ORDER_FAILED"));
    } finally {
      cancelLoading.value = false;
    }
  };

  const {hideDialog} = dialogStoreV2.showDialog({
    titleComponent: h(AbstractDialogTitle, {
      title: $t("REGISTER.PAYMENT_TITLE"),
    }),
    bodyComponent: () => h(DialogBody, {
      orderDetails: currentOrderDetails.value,
      paymentInfo: currentPaymentInfo.value,
      paymentSuccessful: paymentSuccessful.value,
      refreshLoading: refreshLoading.value,
      cancellationPending: cancellationPending.value,
      cancellationRemainingSeconds: cancellationRemainingSeconds.value,
    }),
    footerComponent: () => h(DialogFooter, {
      paymentSuccessful: paymentSuccessful.value,
      refreshLoading: refreshLoading.value,
      cancelLoading: cancelLoading.value,
      cancellationPending: cancellationPending.value,
      onRefresh: refreshStatus,
      onCancelOrder: cancelOrder,
      onDecline: () => {
        resolution.resolve([false]);
        hideDialog();
      },
    }),
    options: {
      dialogClass: "!w-[95%] !max-w-[95%] sm:!max-w-[500px]",
      dialogMinWidth: "",
      dialogMinHeight: "",
      onClose: () => {
        if (closeTimer) {
          clearTimeout(closeTimer);
          closeTimer = null;
        }
        stopCancellationTimer();
        resolution.resolve([false]);
      },
      allowCloseByEscape: true,
    },
  });

  return resolution.promise!;
}
